/**
 * Item keys.
 *
 * Per-item merge needs every mergeable thing to have a stable identity that
 * BOTH devices derive identically. Keys are derived from the data, never
 * stored alongside it, so they cannot drift out of step with the thing they
 * name.
 *
 * The prefix is not decoration: it keeps a manual item's id from colliding
 * with an ingredient id in the one flat `itemMeta` map, and it lets the merge
 * result be routed back to the right array without a second lookup table.
 */

export const dinnerKey = (date: string): string => `dinner:${date}`;
export const lunchKey = (date: string, personId: string): string => `lunch:${date}:${personId}`;
export const stapleKey = (ingredientId: string): string => `staple:${ingredientId}`;
export const manualKey = (id: string): string => `manual:${id}`;
export const pantryKey = (ingredientId: string): string => `pantry:${ingredientId}`;

/**
 * The shopping list ticks a plan by ingredient id OR by manual item id — the
 * `ticked` map already mixes both, and `removeManual` relies on that. So the
 * tick key wraps whatever that map is keyed by rather than assuming which.
 */
export const tickKey = (tickedKey: string): string => `tick:${tickedKey}`;

export const favKey = (mealId: string): string => `fav:${mealId}`;
export const cookKey = (mealId: string, cookedOn: string): string => `cook:${mealId}:${cookedOn}`;
export const libStapleKey = (ingredientId: string): string => `libstaple:${ingredientId}`;

/** One recipe is one item: last-write-wins on the whole recipe. */
export const recipeKey = (mealId: string): string => `recipe:${mealId}`;

/**
 * The chosen store is a property of the week, not of any item in it — but it
 * still has to merge, so it gets a key of its own rather than riding along with
 * the plan row and reintroducing whole-plan last-write-wins by the back door.
 */
export const PLAN_STORE_KEY = 'plan:store';

export type ItemKind =
  | 'plan' | 'dinner' | 'lunch' | 'staple' | 'manual' | 'pantry' | 'tick'
  | 'fav' | 'cook' | 'libstaple' | 'recipe';

/** The kind is the segment before the first colon. */
export function kindOf(key: string): ItemKind | null {
  const kind = key.slice(0, key.indexOf(':'));
  switch (kind) {
    case 'plan': case 'dinner': case 'lunch': case 'staple': case 'manual':
    case 'pantry': case 'tick': case 'fav': case 'cook': case 'libstaple': case 'recipe':
      return kind;
    default:
      return null;
  }
}
